/**
 * Sidebar (Redesign 03.09.2026): fünf Bereiche statt dreizehn Einträge.
 *
 *   Heute · Kunden · Vertrieb · Finanzen · System
 *
 * Ein Bereich zeigt seine Unterpunkte nur, wenn man drin ist — die
 * anderen bleiben eine Zeile. Alles andere erreicht man mit ⌘K.
 * Die Kundenrolle behält ihre flache Liste (Anfragen, Analytics, …).
 * Die Rolle „vertrieb" (06.09.2026) bekommt nur den Vertriebs-Ausschnitt:
 * Betriebe · CRM · To-Dos — kein Heute, keine Kunden, keine Finanzen.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { Gauge, Building2, Inbox, Users, CheckSquare, Search, Wallet, CreditCard, PieChart, Radio, Settings, FolderKanban, SlidersHorizontal, FileText, FileBarChart, BookOpen, Command } from 'lucide-react';
import { useAuthStore } from '@/hooks/useAuth';
import { useLayout } from './Layout';
import { useCommandPalette } from '@/components/CommandPalette';

type Eintrag = { to: string; label: string; icon: React.ComponentType<{ className?: string }>; exact?: boolean };
type Bereich = Eintrag & { kinder?: Eintrag[] };

const ADMIN: Bereich[] = [
  // Leitwarte (24.09.2026): die eine Startseite. Heute, Vertriebsanalyse,
  // Analytics gesamt und Statistiken sind darin aufgegangen und bleiben nur
  // noch per URL oder ⌘K erreichbar.
  { to: '/leitwarte', label: 'Leitwarte', icon: Gauge, exact: true },
  { to: '/kunden', label: 'Kunden', icon: Building2, kinder: [
    { to: '/kunden', label: 'Alle Kunden', icon: Building2, exact: true },
    { to: '/leads', label: 'Alle Anfragen', icon: Inbox },
    { to: '/monatsbericht', label: 'Monatsberichte', icon: FileBarChart },
    { to: '/analytics/live', label: 'Live-Besucher', icon: Radio },
  ] },
  { to: '/betriebe', label: 'Pipeline', icon: Users, kinder: [
    // „Betriebe" ist das Master-CRM (Leadpool + Chancen je Produkt, 06.09.2026)
    { to: '/betriebe', label: 'Betriebe', icon: Building2 },
    { to: '/crm', label: 'CRM', icon: Users },
    { to: '/todos', label: 'Vertriebsablauf', icon: CheckSquare },
    { to: '/potenzial', label: 'Potenzialanalyse', icon: Search },
  ] },
  { to: '/finanzen', label: 'Geld', icon: Wallet, kinder: [
    { to: '/finanzen', label: 'Finanzplan', icon: Wallet },
    { to: '/abrechnung', label: 'Abrechnung', icon: CreditCard },
  ] },
  { to: '/projects', label: 'Einstellungen', icon: Settings, kinder: [
    { to: '/projects', label: 'Projekte', icon: FolderKanban },
    { to: '/einrichtung', label: 'Einrichtung', icon: SlidersHorizontal },
    { to: '/submissions', label: 'Formulare', icon: FileText },
    { to: '/dokumentation', label: 'Dokumentation', icon: BookOpen },
  ] },
];
const VERTRIEB: Bereich[] = [
  { to: '/betriebe', label: 'Betriebe', icon: Building2 },
  { to: '/crm', label: 'CRM', icon: Users },
  { to: '/todos', label: 'Vertriebsablauf', icon: CheckSquare },
];
const KUNDE: Bereich[] = [
  { to: '/leads', label: 'Anfragen', icon: Inbox },
  { to: '/analytics', label: 'Analytics', icon: PieChart },
  { to: '/monatsbericht', label: 'Monatsbericht', icon: FileBarChart },
  { to: '/submissions', label: 'Formulare', icon: FileText },
  { to: '/abrechnung', label: 'Abrechnung', icon: CreditCard },
];

const trifft = (pfad: string, e: Eintrag) => (e.exact ? pfad === e.to : pfad === e.to || pfad.startsWith(e.to + '/'));

export default function Sidebar() {
  const { user } = useAuthStore();
  const { sidebarOpen, setSidebarOpen } = useLayout();
  const { open } = useCommandPalette();
  const { pathname } = useLocation();
  const isAdmin = user?.role === 'admin';
  const bereiche = isAdmin ? ADMIN : user?.role === 'vertrieb' ? VERTRIEB : KUNDE;

  // Welcher Bereich ist „offen"? Der, dessen Unterpunkt (oder er selbst) zur URL passt.
  const offener = bereiche.find((b) => (b.kinder ? b.kinder.some((k) => trifft(pathname, k)) : trifft(pathname, b)))
    // /kunden/:id und /leads/:id gehören zu „Kunden"
    || (isAdmin && (pathname.startsWith('/kunden') || pathname.startsWith('/leads')) ? bereiche[1] : undefined);

  return (
    <aside className={`fixed top-14 bottom-0 left-0 w-56 bg-white border-r border-gray-200 z-40 transform transition-transform duration-200 ease-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
      <nav className="h-full flex flex-col overflow-y-auto py-3 px-2">
        <div className="space-y-0.5">
          {bereiche.map((b) => {
            const aktiv = offener === b;
            const Icon = b.icon;
            return (
              <div key={b.label}>
                <NavLink
                  to={b.to}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2.5 px-2.5 py-[7px] rounded-lg text-[13px] font-medium transition ${aktiv ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {b.label}
                </NavLink>
                {aktiv && b.kinder && (
                  <div className="mt-0.5 mb-1.5 ml-[21px] pl-2.5 border-l border-gray-200 space-y-0.5">
                    {b.kinder.map((k) => {
                      const kAktiv = trifft(pathname, k) || (k.to === '/kunden' && pathname.startsWith('/kunden/') && !pathname.startsWith('/kunden/verwalten')) || (k.to === '/leads' && pathname.startsWith('/leads/'));
                      return (
                        <NavLink key={k.to} to={k.to} onClick={() => setSidebarOpen(false)} className={`block px-2 py-[5px] rounded-md text-[13px] transition ${kAktiv ? 'text-blue-700 font-medium bg-blue-50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                          {k.label}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button onClick={open} className="mt-4 mx-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-dashed border-gray-200 text-xs text-gray-400 hover:text-gray-700 hover:border-gray-300">
          <Command className="w-3.5 h-3.5" /> Suchen <kbd className="ml-auto text-[10px] border border-gray-200 rounded px-1">⌘K</kbd>
        </button>

        <div className="mt-auto pt-3 border-t border-gray-100">
          <p className="px-2 text-[10px] leading-tight text-gray-300">Build {__BUILD_ID__}</p>
        </div>
      </nav>

      {sidebarOpen && (
        <button onClick={() => setSidebarOpen(false)} className="lg:hidden absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      )}
    </aside>
  );
}
